import { useState, useEffect, useCallback } from 'react';
import { useLang } from '@/hooks/useLang';

const BASE = 'https://www.yamamoto-ss.co.jp/sp_recycle';

const cases = [
  { href: `${BASE}/case/case_55.html`, img: `${BASE}/case/data_img/case_55_t.JPG`, catchEn: 'Reduced waste material by ~70% using Purasiru plastic ID device', catchJa: '「ぷらしる」の活用で廃材処理量を約70%カット', industryEn: 'Recycler', industryJa: 'リサイクル業者', useEn: 'Material sorting after plastic product disassembly', useJa: '製品プラスチックを分解した後の材質選別', numEn: '70%', numJa: '70%', metricEn: 'Waste Reduction', metricJa: '廃材削減' },
  { href: `${BASE}/case/case_54.html`, img: `${BASE}/case/data_img/case_54_t.jpg`, catchEn: 'Upgraded capacity & lighter ingots cut processing time in half', catchJa: '能力アップ・インゴット軽量化で、作業時間が半減', industryEn: 'Waste Processor', industryJa: '廃棄物処理業者', useEn: 'EPS foam compaction', useJa: '発泡スチロールの減容', numEn: '50%', numJa: '50%', metricEn: 'Time Saved', metricJa: '作業時間削減' },
  { href: `${BASE}/case/case_52.html`, img: `${BASE}/case/data_img/case_52_t.jpeg`, catchEn: 'Using Purasiru to upcycle ocean plastic waste', catchJa: '「ぷらしる」を活用し、海洋プラのアップサイクルに取り組んでいます', industryEn: 'Environmental', industryJa: '環境事業', useEn: 'Ocean plastic upcycling', useJa: '海洋プラのアップサイクル', numEn: '100%', numJa: '100%', metricEn: 'Ocean Plastic', metricJa: '海洋プラ活用' },
  { href: `${BASE}/case/case_53.html`, img: `${BASE}/case/data_img/case_53_t.jpg`, catchEn: 'Automated PET/can/bottle sorting boosted productivity', catchJa: 'ペットボトル・缶・瓶の自動選別で、生産性向上を実現', industryEn: 'Recycler', industryJa: 'リサイクル業者', useEn: 'Automated beverage container sorting', useJa: '飲料容器の自動選別', numEn: '3x', numJa: '3倍', metricEn: 'Productivity', metricJa: '生産性向上' },
  { href: `${BASE}/case/case_51.html`, img: `${BASE}/case/data_img/case_51_t.jpg`, catchEn: 'Upgraded from 50kg/h unit to large-scale machine as volume grew', catchJa: '処理量増加に合わせて50kg/h処理機から大型機に更新', industryEn: 'Waste Processor', industryJa: '廃棄物処理業者', useEn: 'EPS foam compaction', useJa: '発泡スチロールの減容', numEn: '10x', numJa: '10倍', metricEn: 'Capacity', metricJa: '処理能力' },
  { href: `${BASE}/case/case_50.html`, img: `${BASE}/case/data_img/case_50_t.jpeg`, catchEn: 'Efficient pre-processing of PET bottles from increased tourism', catchJa: '観光客の増加により増えたペットボトルを効率的に前処理', industryEn: 'Waste Processor', industryJa: '廃棄物処理業者', useEn: 'PET bottle pre-processing', useJa: 'ペットボトルの前処理', numEn: '2x', numJa: '2倍', metricEn: 'Throughput', metricJa: '処理量' },
  { href: `${BASE}/case/case_49.html`, img: `${BASE}/case/data_img/case_49_t.jpg`, catchEn: 'Chose Purasiru over competitors for its ease of use', catchJa: '他社製の判別装置と比較検討。操作性で「ぷらしる」を選びました。', industryEn: 'Waste Processor', industryJa: '廃棄物処理業者', useEn: 'Material verification at intake', useJa: '原料受入時の材質確認', numEn: '#1', numJa: '1位', metricEn: 'Ease of Use', metricJa: '操作性' },
  { href: `${BASE}/case/case_48.html`, img: `${BASE}/case/data_img/case_48_t.jpg`, catchEn: 'Switched to electric heat model — handles XPS too, cut costs', catchJa: 'XPSも減容できる電熱式への更新でコストダウンに貢献', industryEn: 'Waste Processor', industryJa: '廃棄物処理業者', useEn: 'EPS & XPS foam compaction', useJa: '発泡スチロール・XPSの減容', numEn: '30%', numJa: '30%', metricEn: 'Cost Down', metricJa: 'コスト削減' },
];

export default function CasesSection() {
  const { lang, t } = useLang();
  const [active, setActive] = useState(0);
  const [animating, setAnimating] = useState(false);

  const shift = useCallback((dir: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setActive(s => (s + dir + cases.length) % cases.length);
      setAnimating(false);
    }, 300);
  }, [animating]);

  useEffect(() => {
    const t = setInterval(() => shift(1), 5500);
    return () => clearInterval(t);
  }, [shift]);

  const featured = cases[active];
  const rest = [
    cases[(active + 1) % cases.length],
    cases[(active + 2) % cases.length],
    cases[(active + 3) % cases.length],
  ];

  return (
    <section className="py-24 bg-[#f7f6f4]">
      <div className="max-w-screen-xl mx-auto px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-px bg-[#c0392b]"></div>
              <span className="text-[#c0392b] text-xs font-bold uppercase tracking-[0.2em]">
                {t('Real Results', '実績')}
              </span>
            </div>
            <h2 className="text-4xl font-black text-[#0a0a0a]">{t('Case Studies', '導入事例')}</h2>
          </div>
          <a
            href={`${BASE}/case/`}
            className="group inline-flex items-center gap-2 text-sm font-bold text-[#0a0a0a] border-b-2 border-[#c0392b] pb-1 hover:text-[#c0392b] transition-colors whitespace-nowrap self-start md:self-auto"
          >
            {t('View All Case Studies', 'すべての導入事例')}
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </a>
        </div>

        {/* Magazine layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Featured large card */}
          <a
            href={featured.href}
            className={`group lg:col-span-2 relative overflow-hidden cursor-pointer transition-opacity duration-300 ${animating ? 'opacity-0' : 'opacity-100'}`}
            style={{ minHeight: '420px' }}
          >
            <img
              src={featured.img}
              alt={lang === 'en' ? featured.catchEn : featured.catchJa}
              className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>

            {/* Metric badge */}
            <div className="absolute top-6 right-6 bg-[#c0392b] text-white px-4 py-3 text-center">
              <div className="text-2xl font-black leading-none">{lang === 'en' ? featured.numEn : featured.numJa}</div>
              <div className="text-[9px] uppercase tracking-widest mt-0.5 opacity-80">{lang === 'en' ? featured.metricEn : featured.metricJa}</div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] bg-white/20 text-white px-2 py-0.5 uppercase tracking-widest">
                  {lang === 'en' ? featured.industryEn : featured.industryJa}
                </span>
              </div>
              <p className="text-white text-xl font-bold leading-snug mb-2">
                {lang === 'en' ? featured.catchEn : featured.catchJa}
              </p>
              <p className="text-white/60 text-xs">
                {lang === 'en' ? featured.useEn : featured.useJa}
              </p>
            </div>
          </a>

          {/* Side cards */}
          <div className={`flex flex-col gap-4 transition-opacity duration-300 ${animating ? 'opacity-0' : 'opacity-100'}`}>
            {rest.map((c, i) => (
              <a
                key={`${c.href}-${i}`}
                href={c.href}
                className="group relative overflow-hidden cursor-pointer flex-1"
                style={{ minHeight: '120px' }}
              >
                <img
                  src={c.img}
                  alt={lang === 'en' ? c.catchEn : c.catchJa}
                  className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/30"></div>
                <div className="absolute inset-0 p-4 flex flex-col justify-between">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] bg-white/20 text-white px-1.5 py-0.5 uppercase tracking-widest">
                      {lang === 'en' ? c.industryEn : c.industryJa}
                    </span>
                    <span className="text-[#c0392b] text-sm font-black">{lang === 'en' ? c.numEn : c.numJa}</span>
                  </div>
                  <p className="text-white text-xs font-bold leading-snug">
                    {lang === 'en' ? c.catchEn : c.catchJa}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center gap-2">
            {cases.map((_, i) => (
              <button
                key={i}
                onClick={() => { if (!animating) { setAnimating(true); setTimeout(() => { setActive(i); setAnimating(false); }, 300); } }}
                className={`transition-all duration-300 cursor-pointer ${i === active ? 'w-8 h-1 bg-[#c0392b]' : 'w-4 h-1 bg-[#ccc] hover:bg-[#999]'}`}
                aria-label={`Case ${i + 1}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => shift(-1)}
              className="w-10 h-10 flex items-center justify-center border border-[#ddd] hover:border-[#c0392b] hover:text-[#c0392b] transition-all cursor-pointer"
              aria-label="Previous"
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <button
              onClick={() => shift(1)}
              className="w-10 h-10 flex items-center justify-center border border-[#ddd] hover:border-[#c0392b] hover:text-[#c0392b] transition-all cursor-pointer"
              aria-label="Next"
            >
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
